// src/pages/PopiaNotice.jsx
import React from 'react';
import { Link } from 'react-router-dom';

const PopiaNotice = () => {
    return (
        <div className="container mx-auto px-4 py-12 max-w-4xl">
            <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-2xl mb-4">
                    <i className="fas fa-gavel text-white text-2xl"></i>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-gradpath-dark mb-3">
                    POPIA Compliance Notice
                </h1>
                <p className="text-gray-600">
                    Protection of Personal Information Act (POPIA) Compliance
                </p>
                <p className="text-sm text-gray-500 mt-2">Last updated: June 27, 2026</p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 md:p-8 space-y-6">
                <section className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
                    <p className="text-green-800 text-sm">
                        <i className="fas fa-check-circle mr-2"></i>
                        GradPath is fully committed to complying with the Protection of Personal Information Act (POPIA) of South Africa.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-info-circle text-gradpath-green text-lg"></i>
                        Overview
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        The Protection of Personal Information Act (POPIA) is South Africa's data protection law that regulates how personal information is collected, processed, stored, and shared. This notice explains how GradPath complies with POPIA requirements.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-database text-gradpath-green text-lg"></i>
                        Lawful Processing Grounds
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-3">
                        We process personal information based on the following lawful grounds:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li><strong>Consent:</strong> You provide explicit consent when you create an account and use our services</li>
                        <li><strong>Contractual Necessity:</strong> Processing is necessary to provide our services to you</li>
                        <li><strong>Legal Obligation:</strong> Processing is required to comply with applicable laws</li>
                        <li><strong>Legitimate Interest:</strong> Processing is necessary for our legitimate business interests, such as improving our platform</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-shield-alt text-gradpath-green text-lg"></i>
                        Your POPIA Rights
                    </h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-bold text-gradpath-dark">Right to Access</h3>
                            <p className="text-sm text-gray-600 mt-1">You can request a copy of your personal information that we hold.</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-bold text-gradpath-dark">Right to Correction</h3>
                            <p className="text-sm text-gray-600 mt-1">You can request correction of inaccurate or incomplete information.</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-bold text-gradpath-dark">Right to Deletion</h3>
                            <p className="text-sm text-gray-600 mt-1">You can request deletion of your personal information.</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-bold text-gradpath-dark">Right to Object</h3>
                            <p className="text-sm text-gray-600 mt-1">You can object to the processing of your personal information.</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-bold text-gradpath-dark">Right to Withdraw Consent</h3>
                            <p className="text-sm text-gray-600 mt-1">You can withdraw your consent for data processing at any time.</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-bold text-gradpath-dark">Right to Data Portability</h3>
                            <p className="text-sm text-gray-600 mt-1">You can request your data in a portable format.</p>
                        </div>
                    </div>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-users text-gradpath-green text-lg"></i>
                        Candidate Information & Recruiters
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        As a candidate on GradPath, your profile information is made available to recruiters and companies that use our platform for talent sourcing. This includes:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
                        <li>Professional information (skills, experience, education)</li>
                        <li>Contact information (email, phone) - only to verified recruiters</li>
                        <li>Career preferences and aspirations</li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed mt-3">
                        <strong>Note:</strong> Sensitive information such as your ID number is <strong>never</strong> shared with recruiters.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-envelope text-gradpath-green text-lg"></i>
                        Marketing Communications
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        By creating an account, you consent to receiving marketing communications from us. You can opt-out at any time by clicking the unsubscribe link in our emails or updating your preferences in your account settings.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-lock text-gradpath-green text-lg"></i>
                        Data Security Measures
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-3">
                        We implement appropriate technical and organizational measures to protect your personal information:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li>Encryption of data at rest and in transit</li>
                        <li>Secure authentication and access controls</li>
                        <li>Regular security assessments and audits</li>
                        <li>Staff training on data protection</li>
                        <li>Data breach response procedures</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-database text-gradpath-green text-lg"></i>
                        Data Retention
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        We retain your personal information for as long as your account is active. Upon account deletion, your data is permanently removed from our systems within 30 days, unless we are required to retain it for legal or regulatory purposes.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-exclamation-triangle text-gradpath-green text-lg"></i>
                        Data Breach Notification
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        In the event of a data breach that may compromise your personal information, we will notify you and the Information Regulator within 72 hours of becoming aware of the breach, as required by POPIA.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-edit text-gradpath-green text-lg"></i>
                        Changes to This Notice
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        We may update this POPIA Compliance Notice from time to time. Changes become effective immediately upon posting. Your continued use of the Platform after changes constitutes acceptance of the updated notice.
                    </p>
                </section>

                <section className="bg-gray-50 p-4 rounded-lg">
                    <h2 className="text-lg font-bold text-gradpath-dark mb-2 flex items-center gap-2">
                        <i className="fas fa-envelope text-gradpath-green"></i>
                        Contact Our Data Protection Officer
                    </h2>
                    <p className="text-gray-700 text-sm">
                        <strong>Email:</strong> <a href="mailto:dpo@gradpath.co.za" className="text-gradpath-green hover:underline">dpo@gradpath.co.za</a><br />
                        <strong>Information Regulator (South Africa):</strong><br />
                        P.O. Box 31533, Braamfontein, 2017<br />
                        <strong>Website:</strong> <a href="https://www.justice.gov.za/inforeg/" target="_blank" rel="noopener noreferrer" className="text-gradpath-green hover:underline">www.justice.gov.za/inforeg/</a><br />
                        <strong>Email:</strong> <a href="mailto:inforeg@justice.gov.za" className="text-gradpath-green hover:underline">inforeg@justice.gov.za</a>
                    </p>
                </section>

                <div className="text-center pt-4">
                    <Link to="/" className="inline-flex items-center gap-2 text-gradpath-green hover:underline">
                        <i className="fas fa-arrow-left"></i>
                        Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default PopiaNotice;